/**
 * Phase 99 — Frontend permission gate.
 *
 * Single source of truth for "can this user see / do X?". Keep it dependency-free
 * and synchronous so call sites can use it inline in JSX without effects.
 *
 * The PERMISSION KEYS below MUST stay in lockstep with the backend's
 * PERMISSION_CATALOG in server.py.
 */

// Roles that hold every permission implicitly. They never go through ACL checks.
const SUPERUSER_ROLES = new Set(['academy_owner', 'super_admin']);

/**
 * Check whether the given user holds the named permission.
 * @param {{role: string, permissions?: string[]}|null|undefined} user
 * @param {string} key permission key (e.g. 'view_finance')
 * @returns {boolean}
 */
export function hasPermission(user, key) {
  if (!user) return false;
  if (SUPERUSER_ROLES.has(user.role)) return true;
  if (!Array.isArray(user.permissions)) return false;
  return user.permissions.includes(key);
}

/**
 * Check that ALL listed permissions are held by the user.
 * @param {object|null} user
 * @param {string[]} keys
 */
export function hasAllPermissions(user, keys) {
  return keys.every((k) => hasPermission(user, k));
}

/**
 * Check that AT LEAST ONE listed permission is held by the user.
 * @param {object|null} user
 * @param {string[]} keys
 */
export function hasAnyPermission(user, keys) {
  return keys.some((k) => hasPermission(user, k));
}

// Mirror of backend labels so we can render the matrix without a network call
// on first paint. The actual catalogue/groups come from /api/permissions/catalog
// at runtime — this is just a fallback for offline / loading states.
export const FALLBACK_PERMISSION_CATALOG = {
  permissions: [
    'view_finance', 'generate_fees', 'view_kpi', 'manage_crm',
    'write_attendance', 'write_progress_cards', 'manage_broadcasts',
    'view_students', 'view_invoices',
  ],
  labels: {
    view_finance: 'Finance Tab Access',
    generate_fees: 'Trigger Fee Generation',
    view_kpi: 'KPI Dashboard',
    manage_crm: 'Lead Management (CRM)',
    write_attendance: 'Write Attendance',
    write_progress_cards: 'Write Progress Cards',
    manage_broadcasts: 'Manage Broadcasts',
    view_students: 'Students Directory',
    view_invoices: 'View Invoices',
  },
  groups: {
    'Back-Office': ['view_finance', 'generate_fees', 'view_kpi', 'view_invoices'],
    'Operations': ['write_attendance', 'write_progress_cards', 'view_students'],
    'Outreach': ['manage_crm', 'manage_broadcasts'],
  },
};
